import { useCallback, useEffect, useState } from 'react';
import Table, { Order } from '../../../components/Table';
import { IPaginationData } from '../../../api/base';
import moment from 'moment';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { Add, Delete, Edit, Search } from '@mui/icons-material';
import { toast } from 'react-toastify';
import theme from '../../../theme';
import _ from 'lodash';
import { EPartnerType, IPartner } from '../../../models/partner.model';
import partnerApi from '../../../api/partner.api';
import { LogoImage } from '../../styled';
import ImageUploadWithPreview from '../../../components/ImageUpload';
import PartnerTypeSelect from '../../../components/PartnerTypeSelect';

export default function PartnerListPage() {
  const [type, setType] = useState(EPartnerType.Investor);
  const [dataSource, setDataSource] = useState<IPaginationData<IPartner>>({
    limit: 10,
    page: 1,
    total: 0,
    total_page: 0,
    records: [],
  });
  const [loading, setLoading] = useState(false);
  const [[orderBy, order], setOrder] = useState<
    [string | undefined, Order | undefined]
  >(['priority', 'desc']);

  const [filter, setFilter] = useState({
    keyword: '',
    page: 1,
    limit: 10,
  });

  const [currItem, setCurrItem] = useState<Partial<IPartner> | undefined>();
  const [currDeletedId, setCurrDeletedId] = useState<string | undefined>();
  const [deleting, setDeleting] = useState(false);

  const loadData = useCallback(() => {
    setLoading(true);
    partnerApi
      .getList({
        ...filter,
        type,
        order: order?.toUpperCase() as 'ASC' | 'DESC',
        sort_by: orderBy,
      })
      .then((res) => {
        setDataSource(res.data);
      })
      .catch((e) => {
        toast.error(e.message ?? e.toString());
      })
      .finally(() => {
        setLoading(false);
      });
  }, [order, orderBy, filter, type]);

  useEffect(loadData, [loadData]);

  return (
    <>
      <Box
        sx={(theme) => ({
          display: 'flex',
          justifyContent: 'space-between',
          p: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
        })}
      >
        <Typography variant="h3">Partner</Typography>
        <div
          style={{
            display: 'flex',
            gap: theme.spacing(4),
          }}
        >
          <form
            onSubmit={(e) => {
              e.preventDefault();

              const formData = new FormData(e.currentTarget);
              setFilter({
                ...filter,
                page: 1,
                keyword: (formData.get('search') || '').toString(),
              });
            }}
          >
            <TextField
              placeholder="Search"
              variant="outlined"
              size="small"
              name="search"
              sx={{
                '& .MuiInputBase-root': {
                  pr: 0,
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Button
                      type="submit"
                      sx={{ minWidth: 48, borderRadius: 0 }}
                    >
                      <Search />
                    </Button>
                  </InputAdornment>
                ),
              }}
            />
          </form>
          <div>
            <Button
              variant="contained"
              color="success"
              startIcon={<Add />}
              onClick={() => {
                setCurrItem({
                  type,
                  priority: 1,
                });
              }}
            >
              Create new
            </Button>
          </div>
        </div>
      </Box>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          variant="fullWidth"
          value={type}
          onChange={(ev, v) => setType(v)}
          sx={{
            '& .MuiTab-root': {
              fontWeight: 700,
              fontSize: 18,
            },
          }}
        >
          {Object.values(EPartnerType).map((t) => (
            <Tab key={t} label={_.capitalize(_.words(t).join(' '))} value={t} />
          ))}
        </Tabs>
      </Box>
      <Table
        onOrderChange={(orderBy, order) => setOrder([orderBy!, order!])}
        order={order}
        orderBy={orderBy}
        loading={loading}
        // onClick={setCurrItem}
        pagination={{
          limit: dataSource.limit,
          total: dataSource.total,
          page: dataSource.page - 1,
          onPageChange: (event, page) => {
            setFilter({
              ...filter,
              page: page + 1,
            });
          },
          onLimitChange: (ev) => {
            setFilter({
              ...filter,
              limit: +ev.target.value,
              page: 1,
            });
          },
        }}
        columns={[
          {
            key: 'priority',
            title: 'Index',
            dataKey: 'priority',
            sortable: true,
          },
          {
            key: 'logo',
            title: 'Logo',
            dataKey: 'logo',
            render: (data) =>
              data.logo ? (
                <LogoImage src={process.env.REACT_APP_API_URI + data.logo} />
              ) : null,
          },
          {
            key: 'name',
            title: 'Name',
            dataKey: 'name',
            sortable: true,
          },

          {
            key: 'created_at',
            title: 'Created at',
            dataKey: 'created_at',
            sortable: true,
            render: (data) =>
              moment(data.created_at).format('DD/MM/YYYY HH:mm'),
          },
          {
            key: 'updated_at',
            title: 'Updated at',
            dataKey: 'updated_at',
            sortable: true,
            render: (data) =>
              moment(data.updated_at).format('DD/MM/YYYY HH:mm'),
          },
          {
            key: 'link',
            title: 'Link',
            dataKey: 'link',
            sortable: true,
            render: (data) => (
              <>
                {data.link_en && (
                  <div>
                    <a href={data.link_en} target="_blank" rel="noreferrer">
                      {data.link_en}
                    </a>
                  </div>
                )}
                {data.link_vi && (
                  <div>
                    <a href={data.link_vi} target="_blank" rel="noreferrer">
                      {data.link_vi}
                    </a>
                  </div>
                )}
              </>
            ),
          },
          {
            key: 'actions',
            title: 'Actions',
            dataKey: 'id',
            render: (data) => (
              <>
                <IconButton onClick={() => setCurrItem(data)}>
                  <Edit />
                </IconButton>
                <IconButton onClick={() => setCurrDeletedId(data.id)}>
                  <Delete />
                </IconButton>
              </>
            ),
          },
        ]}
        dataSource={dataSource.records}
        rowKey={(data) => data.id}
      />

      <Dialog
        open={!!currDeletedId}
        onClose={() => setCurrDeletedId(undefined)}
      >
        <DialogContent>
          <DialogContentText color={'black'}>
            This action cannot be revert. Do you want to delete this submission?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            autoFocus
            disabled={deleting}
            onClick={() => setCurrDeletedId(undefined)}
            size="small"
          >
            Cancel
          </Button>
          <Button
            size="small"
            disabled={deleting}
            onClick={() => {
              if (currDeletedId) {
                setDeleting(true);
                partnerApi
                  .remove(currDeletedId)
                  .then(() => {
                    loadData();
                    toast.success('Delete partner successful');
                  })
                  .catch((e) => {
                    toast.error(e.message ?? e.toString());
                  })
                  .finally(() => {
                    setCurrDeletedId(undefined);
                    setDeleting(false);
                  });
              }
            }}
            color="error"
          >
            {deleting ? <CircularProgress size={16} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={!!currItem} onClose={() => setCurrDeletedId(undefined)}>
        <DialogTitle>
          {currItem?.id ? 'Update partner' : 'Create new partner'}
        </DialogTitle>
        <DialogContent
          sx={{
            overflow: 'auto',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Typography p={1}>Logo</Typography>

            <ImageUploadWithPreview
              value={currItem?.logo}
              onChange={(value) => {
                if (currItem) {
                  setCurrItem({
                    ...currItem,
                    logo: value,
                  });
                }
              }}
            />
            <TextField
              label="Name"
              size="small"
              margin="dense"
              type="text"
              inputProps={{
                min: 1,
              }}
              value={currItem?.name}
              onChange={(e) => {
                if (currItem) {
                  setCurrItem({
                    ...currItem,
                    name: e.currentTarget.value,
                  });
                }
              }}
            />
            {currItem?.type ? (
              <PartnerTypeSelect
                margin="normal"
                value={currItem.type}
                onChange={(value) => {
                  if (currItem) {
                    setCurrItem({
                      ...currItem,
                      type: value,
                    });
                  }
                }}
              />
            ) : null}
            <TextField
              label="Index"
              size="small"
              margin="normal"
              type="number"
              inputProps={{
                min: 1,
              }}
              value={currItem?.priority ?? 1}
              onChange={(e) => {
                if (currItem) {
                  setCurrItem({
                    ...currItem,
                    priority: +(e.currentTarget.value || 1),
                  });
                }
              }}
            />
            <TextField
              label="Link English"
              size="small"
              margin="dense"
              type="text"
              inputProps={{
                min: 1,
              }}
              value={currItem?.link_en || ''}
              onChange={(e) => {
                if (currItem) {
                  setCurrItem({
                    ...currItem,
                    link_en: e.currentTarget.value,
                  });
                }
              }}
            />
            <TextField
              label="Link Vietnamese"
              size="small"
              margin="dense"
              type="text"
              inputProps={{
                min: 1,
              }}
              value={currItem?.link_vi || ''}
              onChange={(e) => {
                if (currItem) {
                  setCurrItem({
                    ...currItem,
                    link_vi: e.currentTarget.value,
                  });
                }
              }}
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            autoFocus
            disabled={deleting}
            onClick={() => setCurrItem(undefined)}
            size="small"
          >
            Cancel
          </Button>
          <Button
            size="small"
            disabled={deleting}
            variant="contained"
            onClick={() => {
              if (!currItem) {
                return;
              }
              Promise.resolve()
                .then(() => {
                  setDeleting(true);
                })
                .then(() => {
                  if (currItem?.id) {
                    return partnerApi
                      .update(currItem.id, currItem)
                      .then(() => {
                        toast.success('Update successful');
                      })
                      .catch((e) => {
                        toast.error(e.message ?? e.toString());
                        throw e;
                      });
                  }
                  return partnerApi
                    .create(currItem)
                    .then(() => {
                      toast.success('Update successful');
                    })
                    .catch((e) => {
                      toast.error(e.message ?? e.toString());
                      throw e;
                    });
                })
                .then(() => {
                  loadData();
                })
                .finally(() => {
                  setCurrItem(undefined);
                  setDeleting(false);
                });
            }}
            color="primary"
          >
            {deleting ? (
              <CircularProgress size={16} />
            ) : currItem?.id ? (
              'Update'
            ) : (
              'Create'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
